<!-- Settings shell (matches Company Profile) — wraps the workflows page in the same
     content padding + panel-header layout used across the Settings section. -->
<div class="settings-content workflow-workspace">
  <app-settings-backlink></app-settings-backlink>
  <div class="settings-panel fadein">
    <div class="panel-header wf-panel-head">
      <div>
        <h2 class="panel-title">
          Workflow Engine
          <span class="wf-badge">Enterprise</span>
        </h2>
        <p class="panel-sub">Design, version, and deploy automated conversation logic.</p>
      </div>
      <div class="wf-actions">
        <p-button *ngIf="activeTab === 'journey'" label="Create Journey" icon="pi pi-plus" severity="help" size="small"
                  (click)="goToCreateJourney()" class="shadow-sm transition-transform active:scale-95"></p-button>
        <p-button *ngIf="workflowList.length > 0 && activeTab === 'main'" label="AI Designer" icon="pi pi-sparkles" severity="help" size="small"
                  (click)="openCreateModal()" class="shadow-sm transition-transform active:scale-95"></p-button>
      </div>
    </div>

    <div class="settings-card">

  <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 workflow-kpi">
    <div class="kpi-card" (click)="setStatusFilter('all')">
      <div class="icon-box bg-blue-50 text-blue-600"><i class="pi pi-directions"></i></div>
      <div class="content">
        <label>Total Flows</label>
        <div class="value">{{ workflowList.length }}</div>
      </div>
    </div>
    <div class="kpi-card" (click)="setStatusFilter('active')">
      <div class="icon-box bg-green-50 text-green-600"><i class="pi pi-check-circle"></i></div>
      <div class="content">
        <label>Active</label>
        <div class="value">{{ activeFlowsCount }}</div>
      </div>
    </div>
    <div class="kpi-card" (click)="setStatusFilter('draft')">
      <div class="icon-box bg-slate-50 text-slate-600"><i class="pi pi-file-edit"></i></div>
      <div class="content">
        <label>Drafts</label>
        <div class="value">{{ draftFlowsCount }}</div>
      </div>
    </div>
  </div>

  <!-- Tab bar -->
  <div class="wf-tab-bar">
    <button class="wf-tab" [class.wf-tab--active]="activeTab === 'main'" (click)="setActiveTab('main')">
      <i class="pi pi-sitemap"></i>
      My Flows
      <span class="wf-tab-count">{{ mainFlowsCount }}</span>
    </button>
    <button class="wf-tab" [class.wf-tab--active]="activeTab === 'reusable'" (click)="setActiveTab('reusable')">
      <i class="pi pi-share-alt"></i>
      Reusable Flows
      <span class="wf-tab-count">{{ reusableFlowsCount }}</span>
    </button>
    <button class="wf-tab" [class.wf-tab--active]="activeTab === 'journey'" (click)="setActiveTab('journey')">
      <i class="pi pi-map"></i>
      Journeys
      <span class="wf-tab-count">{{ journeyCount }}</span>
    </button>
  </div>

  <div class="flex w-full mb-6" *ngIf="workflowList.length > 0 && activeTab !== 'journey'">
    <div class="flex items-center gap-2 ml-auto">
      <span class="p-input-icon-left">
        <i class="pi pi-search"></i>
        <input pInputText [(ngModel)]="searchTerm" placeholder="Search workflows..." class="w-64" />
      </span>
    </div>
  </div>

  <div class="workflow-guidance" *ngIf="!loading && workflowList.length === 0 && activeTab === 'main'">
    <div class="guidance-card">
      <div class="guidance-copy">
        <h3>What you can do here</h3>
        <p>Create AI-driven workflows, version them, and publish to your channels in minutes.</p>
      </div>
      <ul class="guidance-list">
        <li>Design multi-step automation flows</li>
        <li>Track active vs. draft workflows</li>
        <li>Launch new versions safely</li>
        <li>Open any flow to edit nodes and logic</li>
      </ul>
      <button pButton label="Create your first workflow" icon="pi pi-plus" class="p-button-sm p-button-outlined guidance-action" (click)="openCreateModal()"></button>
    </div>
  </div>

  <!-- Journey Table -->
  <div class="saas-table-card workflow-table" *ngIf="activeTab === 'journey'">
    <p-table [value]="journeyList" [loading]="loadingJourneys" [rows]="10" [paginator]="true"
             styleClass="p-datatable-sm saas-table" responsiveLayout="scroll">
      <ng-template #header>
        <tr class="bg-slate-50/50 border-b border-slate-100">
          <th class="pl-8 py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">Journey</th>
          <th class="py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">Version</th>
          <th class="py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">Status</th>
          <th class="py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">Source</th>
          <th class="py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">Created</th>
          <th class="pr-8 py-5 text-right text-[11px] font-bold uppercase tracking-widest text-slate-400">Actions</th>
        </tr>
      </ng-template>

      <ng-template #body let-j>
        <tr class="hover:bg-indigo-50/20 transition-all duration-200 group">
          <td class="pl-8 py-5">
            <div class="flex items-center gap-4">
              <div class="avatar-box group-hover:bg-indigo-600 group-hover:text-white transition-all">
                <i class="pi pi-map text-xs"></i>
              </div>
              <div class="flex flex-col">
                <span class="font-bold text-slate-700 tracking-tight">{{ j.flowName }}</span>
                <span class="text-[10px] text-slate-400 font-mono tracking-tighter">{{ j.id.split('-')[0] }}...</span>
              </div>
            </div>
          </td>
          <td class="py-5"><span class="version-badge">v{{ j.version }}</span></td>
          <td class="py-5">
            <p-tag [value]="j.status" [severity]="getSeverity(j.status)" [rounded]="true" styleClass="status-tag"></p-tag>
          </td>
          <td class="py-5 text-xs font-semibold text-slate-500">{{ j.source }}</td>
          <td class="py-5 text-slate-400 text-xs font-medium">{{ j.createdAt | appDate:'date' }}</td>
          <td class="pr-8 py-5 text-right">
            <div class="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <p-button
                icon="pi pi-pencil"
                [text]="true"
                severity="secondary"
                size="small"
                pTooltip="Edit journey"
                (click)="editJourney(j)">
              </p-button>
              <p-button
                icon="pi pi-trash"
                [text]="true"
                [severity]="j.status?.toUpperCase() === 'DRAFT' ? 'danger' : 'secondary'"
                size="small"
                [pTooltip]="j.status?.toUpperCase() === 'DRAFT' ? 'Delete journey' : 'Only DRAFT journeys can be deleted'"
                [disabled]="j.status?.toUpperCase() !== 'DRAFT'"
                (click)="deleteJourney(j)">
              </p-button>
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template #emptymessage>
        <tr>
          <td colspan="6" class="text-center py-12 text-slate-400">
            <i class="pi pi-map text-3xl mb-2 block"></i>
            <p>No journeys found. Build one using the Journey Builder tab inside a workflow.</p>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>

  <div class="saas-table-card workflow-table" *ngIf="activeTab !== 'journey'">
    <p-table [value]="filteredWorkflows" [loading]="loading" [rows]="10" [paginator]="true"
             styleClass="p-datatable-sm saas-table" responsiveLayout="scroll">
      <ng-template #header>
        <tr class="bg-slate-50/50 border-b border-slate-100">
          <th class="pl-8 py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            <button class="sort-header" (click)="setSort('flowName')">
              Flow Identity
              <i class="pi" [ngClass]="getSortIcon('flowName')"></i>
            </button>
          </th>
          <th class="py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            <button class="sort-header" (click)="setSort('version')">
              Version
              <i class="pi" [ngClass]="getSortIcon('version')"></i>
            </button>
          </th>
          <th class="py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            <button class="sort-header" (click)="setSort('status')">
              Status
              <i class="pi" [ngClass]="getSortIcon('status')"></i>
            </button>
          </th>
          <th class="py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            <button class="sort-header" (click)="setSort('source')">
              Source
              <i class="pi" [ngClass]="getSortIcon('source')"></i>
            </button>
          </th>
          <th class="py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            <button class="sort-header" (click)="setSort('updatedAt')">
              Modified Date
              <i class="pi" [ngClass]="getSortIcon('updatedAt')"></i>
            </button>
          </th>
          <th class="pr-8 py-5 text-right text-[11px] font-bold uppercase tracking-widest text-slate-400 th-center">Actions</th>
        </tr>
      </ng-template>

      <ng-template #body let-wf>
        <tr class="hover:bg-blue-50/20 transition-all duration-200 group">
          <td class="pl-8 py-5">
            <div class="flex items-center gap-4">
              <div class="avatar-box group-hover:bg-blue-600 group-hover:text-white transition-all">
                {{ wf.flowName.charAt(0).toUpperCase() }}
              </div>
              <div class="flex flex-col">
                <span class="font-bold text-slate-700 tracking-tight">{{ wf.flowName }}</span>
                <span class="text-[10px] text-slate-400 font-mono tracking-tighter">{{ wf.id.split('-')[0] }}...</span>
              </div>
            </div>
          </td>
          <td class="py-5">
            <span class="version-badge">v{{ wf.version }}</span>
          </td>
          <td class="py-5">
            <p-tag [value]="wf.status" [severity]="getSeverity(wf.status)" [rounded]="true" styleClass="status-tag"></p-tag>
          </td>
          <td class="py-5">
            <div class="flex items-center gap-2 text-xs font-semibold"
                 [ngClass]="isReusableSource(wf.source) ? 'text-violet-600' : 'text-slate-500'">
              <i class="text-[10px]"
                 [ngClass]="wf.source?.toUpperCase() === 'SYSTEM' ? 'pi pi-cog' :
                             wf.source?.toUpperCase() === 'FORKED' ? 'pi pi-share-alt' :
                             wf.source === 'MANUAL' ? 'pi pi-user' : 'pi pi-sparkles'"></i>
              {{ wf.source }}
            </div>
          </td>
          <td class="py-5 text-slate-400 text-xs font-medium">
            {{ wf.updatedAt | appDate:'date' }}
          </td>
          <td class="pr-8 py-5 th-center">
            <div class="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity th-center">
              <p-button icon="pi pi-pencil" [text]="true" severity="secondary" size="small" (click)="goToWorkflow(wf)" pTooltip="Edit Flow"></p-button>
              <p-button icon="pi pi-copy" [text]="true" severity="secondary" size="small" pTooltip="Duplicate"></p-button>
              <p-button icon="pi pi-trash" [text]="true" severity="danger" size="small"></p-button>
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template #emptymessage>
        <tr>
          <td colspan="6" class="text-center py-12 text-slate-400">
            <i class="pi pi-diagram-3 text-3xl mb-2 block"></i>
            <p *ngIf="activeTab === 'main'">No workflows found. Create your first workflow.</p>
            <p *ngIf="activeTab === 'reusable'">No reusable flows available. Forked or system flows will appear here.</p>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>

    </div><!-- /.settings-card -->
  </div><!-- /.settings-panel -->
</div><!-- /.settings-content -->

<p-dialog [(visible)]="showCreateModal" [modal]="true" [header]="'AI Workflow Designer'"
          [style]="{width: '600px'}" class="ai-modal">
  <div class="p-2">
    <div class="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6 flex gap-3">
      <i class="pi pi-sparkles text-blue-600 text-xl"></i>
      <p class="text-sm text-blue-800 leading-relaxed">
        Describe your business process. AI will generate the nodes and transitions.
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-xs font-bold text-slate-500 uppercase tracking-widest">Prompt</label>
      <textarea pInputTextarea rows="6" [(ngModel)]="aiPrompt"
                placeholder="Ask for customer's name, then check if they want a demo..."
                class="w-full border-slate-200 rounded-xl p-4 text-sm focus:ring-blue-500"></textarea>

      <div class="subflow-chips-section">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-xs font-bold text-slate-500 uppercase tracking-widest">Reference Subflows</span>
          <span class="text-xs text-slate-400">(optional)</span>
        </div>

        <div *ngIf="loadingSubflows" class="flex items-center gap-2 text-slate-400 text-xs py-1">
          <i class="pi pi-spin pi-spinner text-xs"></i>
          Loading workflows...
        </div>

        <div *ngIf="!loadingSubflows && subflows.length === 0" class="text-xs text-slate-400 py-1">
          No subflows available.
        </div>

        <div *ngIf="!loadingSubflows && subflows.length > 0" class="subflow-chips">
          <button
            *ngFor="let sf of subflows"
            type="button"
            class="subflow-chip"
            [class.subflow-chip--selected]="isSubflowSelected(sf.flowName)"
            (click)="toggleSubflow(sf.flowName)"
          >
            <i class="pi pi-check-circle text-[10px]" *ngIf="isSubflowSelected(sf.flowName)"></i>
            <i class="pi pi-directions text-[10px]" *ngIf="!isSubflowSelected(sf.flowName)"></i>
            {{ sf.flowName }}
          </button>
        </div>

        <div *ngIf="selectedFlowNames.length > 0" class="mt-2 text-xs text-blue-600 font-medium">
          {{ selectedFlowNames.length }} subflow{{ selectedFlowNames.length > 1 ? 's' : '' }} selected
        </div>
      </div>
    </div>

    <div *ngIf="generating" class="mt-6 ai-generation">
      <div class="ai-generation__status-row">
        <span class="ai-stage">{{ generationStage }}</span>
      </div>
      <div class="ai-generation__message">{{ generationStatusText }}</div>
      <div class="ai-stage-inline" aria-label="Generation stages">
        <span [class.active]="generationStage === 'THINKING' || generationStage === 'ANALYZING' || generationStage === 'CREATING' || generationStage === 'FINALIZING'">Thinking</span>
        <span class="sep">•</span>
        <span [class.active]="generationStage === 'ANALYZING' || generationStage === 'CREATING' || generationStage === 'FINALIZING'">Analyzing</span>
        <span class="sep">•</span>
        <span [class.active]="generationStage === 'CREATING' || generationStage === 'FINALIZING'">Creating</span>
        <span class="sep">•</span>
        <span [class.active]="generationStage === 'FINALIZING'">Finalizing</span>
      </div>
      <div class="ai-progress-track">
        <div class="ai-progress-fill" [style.width.%]="generationProgress"></div>
      </div>
      <div class="ai-generation__hint">Generating workflow architecture...</div>
    </div>
  </div>

  <ng-template #footer>
    <p-button label="Cancel" [text]="true" severity="secondary" (click)="closeCreateModal()"></p-button>
    <p-button label="Generate Architecture" icon="pi pi-bolt" severity="help"
              [loading]="generating" (click)="generateWorkflow()"></p-button>
  </ng-template>
</p-dialog>
